import { View, Text, ActivityIndicator, ScrollView } from 'react-native';
import React, { useMemo, useState, useCallback } from 'react';
import { TabView } from 'react-native-tab-view';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import ReachTabBar from '@/components/General/ReachTabBar';
import CutCard from '@/components/General/CutCard';
import { Query } from '@/utils/react-query/Queries';
import { getReportUrl, REPORT_TYPES } from '@/data/giving';
import GivingOnboardingWebView from '@/components/Giving/GivingOnboardingWebView';
import Unicon from '@/components/General/Unicon';
import usePlatform from '@/utils/hooks/usePlatform';

const TABS = [
    { key: REPORT_TYPES.deposit, title: 'Deposits' },
    { key: REPORT_TYPES.payment, title: 'Payments' },
    { key: REPORT_TYPES.chargeback, title: 'Chargebacks' },
];

const routes = TABS.map(({ key, title }) => ({ key, title }));

function useReportSession({ organizationID, reportType, enabled }) {
    return Query.giving.reportSession({
        organizationID,
        reportType,
        options: { enabled: enabled && !!organizationID },
    });
}

export default function OnlineGivingReportsScreen() {
    const { theme: { colors } } = useUnistyles();
    const { isNative } = usePlatform();
    const [tabIndex, setTabIndex] = useState(0);

    // A Rainforest session is scoped to a single report, so only create one for
    // a tab the first time the user opens it (and keep it alive afterward).
    const [openedTabs, setOpenedTabs] = useState(() => new Set([TABS[0].key]));
    const handleIndexChange = useCallback((next) => {
        setTabIndex(next);
        setOpenedTabs((prev) => {
            if (prev.has(routes[next].key)) return prev;
            const nextSet = new Set(prev);
            nextSet.add(routes[next].key);
            return nextSet;
        });
    }, []);

    const { data: loggedInUser, isLoading: loadingUser } = Query.people.loggedInUser();
    const canViewReports = loggedInUser?.can?.('View Contributions') === true;
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID });

    const depositSession = useReportSession({
        organizationID: loggedInUser?.organizationID,
        reportType: REPORT_TYPES.deposit,
        enabled: canViewReports && openedTabs.has(REPORT_TYPES.deposit),
    });
    const paymentSession = useReportSession({
        organizationID: loggedInUser?.organizationID,
        reportType: REPORT_TYPES.payment,
        enabled: canViewReports && openedTabs.has(REPORT_TYPES.payment),
    });
    const chargebackSession = useReportSession({
        organizationID: loggedInUser?.organizationID,
        reportType: REPORT_TYPES.chargeback,
        enabled: canViewReports && openedTabs.has(REPORT_TYPES.chargeback),
    });

    const sessionsByType = useMemo(() => ({
        [REPORT_TYPES.deposit]: depositSession,
        [REPORT_TYPES.payment]: paymentSession,
        [REPORT_TYPES.chargeback]: chargebackSession,
    }), [depositSession, paymentSession, chargebackSession]);

    const activeTab = routes[tabIndex]?.key ?? REPORT_TYPES.deposit;

    const handleMessage = (data) => {
        if (data?.type === 'webview_error') {
            console.error('[GivingReports] webview error:', data?.detail?.message);
        }
    };

    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderScene = useCallback(({ route: tabRoute }) => {
        const tabKey = tabRoute.key;
        const tab = sessionsByType[tabKey];
        const tabUrl = tab?.data?.sessionKey && tab?.data?.merchantID
            ? getReportUrl({
                type: tabKey,
                sessionKey: tab.data.sessionKey,
                merchantID: tab.data.merchantID,
                timezone: organization?.timezone,
            })
            : null;

        return (
            <ScrollView style={styles.webviewScroll} contentContainerStyle={styles.webviewScrollContent}>
                <CutCard accentColor="primary" style={styles.webviewCard} contentStyle={styles.webviewCardContent}>
                    {tab?.isLoading ? (
                        <View style={styles.center} testID="giving-reporting-loading">
                            <ActivityIndicator color={colors.giving} />
                            <Text style={styles.loadingText}>Loading report session...</Text>
                        </View>
                    ) : tab?.error ? (
                        <View style={styles.center} testID="giving-reporting-error">
                            <Unicon name="alert-circle" size={32} colorVariant="error" />
                            <Text style={styles.errorTitle}>Failed to load reports</Text>
                            <Text style={styles.errorDetail}>
                                {tab.error?.message || 'Could not create a reporting session. Try again.'}
                            </Text>
                        </View>
                    ) : tabUrl && tabKey === activeTab ? (
                        <GivingOnboardingWebView
                            key={tabKey}
                            url={tabUrl}
                            onMessage={handleMessage}
                        />
                    ) : (
                        <View style={styles.center}>
                            <ActivityIndicator color={colors.giving} />
                        </View>
                    )}
                </CutCard>
            </ScrollView>
        );
    }, [sessionsByType, activeTab, organization?.timezone, colors.giving]);

    if (loadingUser) {
        return (
            <View style={styles.loadingContainer} testID="giving-reporting-user-loading">
                <ActivityIndicator color={colors.giving} />
            </View>
        );
    }

    if (!canViewReports) {
        return (
            <View style={styles.permissionContainer} testID="giving-reporting-permission-denied">
                <CutCard accentColor="primary" style={styles.permissionCard} contentStyle={styles.permissionCardContent}>
                    <Unicon name="lock-closed-outline" size={32} colorVariant="textSecondary" />
                    <Text style={styles.permissionTitle}>Online Giving Reports</Text>
                    <Text style={styles.permissionText}>You do not have permission to view this page.</Text>
                </CutCard>
            </View>
        );
    }

    return (
        <View style={styles.container} testID="giving-reporting-screen">
            <View style={styles.hero}>
                <View style={styles.heroIconContainer}>
                    <Unicon name="bar-chart-outline" library="ionicon" colorVariant="onPrimary" size={28} />
                </View>
                <Text style={styles.heroTitle}>Online Giving Reports</Text>
                <Text style={styles.heroDescription}>
                    Review activity and processing details for online gifts.
                </Text>
            </View>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: tabIndex, routes }}
                onIndexChange={handleIndexChange}
                renderTabBar={renderTabBar}
                renderScene={renderScene}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    hero: {
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 20,
        gap: 10,
    },
    heroIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    heroDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 20,
    },
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    permissionContainer: {
        flex: 1,
        padding: spacing.lg,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    permissionCard: {
        width: '100%',
        maxWidth: 440,
        // CutCard owns surface/radius; the shadow stays here so it converts
        // to a clip-path drop-shadow on web.
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    },
    permissionCardContent: {
        padding: spacing.xl,
        alignItems: 'center',
        gap: spacing.sm,
    },
    permissionTitle: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    permissionText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    webviewScroll: {
        flex: 1,
    },
    webviewScrollContent: {
        padding: spacing.md,
    },
    webviewCard: {
        // Rainforest's embedded report is white-themed; passing the fill color
        // through keeps the CutCard's clipped silhouette white too.
        backgroundColor: '#ffffff',
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    },
    webviewCardContent: {
        minHeight: 700,
        overflow: 'hidden',
    },
    center: {
        padding: spacing.xl,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.sm,
    },
    loadingText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    errorTitle: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.error,
    },
    errorDetail: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
}));
